<!--
	Экран установки приложения.

	Показывает шаги того браузера, в котором человек сейчас: раньше здесь был
	один список — про «Поделиться» в Safari, и он же доставался всем, включая
	Android. Читать про Safari на Android бесполезно.

	Где браузер ставить не умеет — так и написано, без правдоподобных, но
	неработающих шагов. А определение по строке браузера иногда ошибается,
	поэтому внизу есть список: можно выбрать свой случай руками.
-->
<script lang="ts">
	import { Button, Icon } from '@andresot/ui';
	import { fade } from 'svelte/transition';
	import { install } from '$stores/install.svelte';
	import { toasts } from '$stores/toasts.svelte';
	import { copyText } from '$utils/clipboard';
	import StepIllustration from './StepIllustration.svelte';
	import { GUIDES, guideById } from './guides';

	let installing = $state(false);
	/** Выбранный руками случай. Пусто — показываем определённый сам. */
	let chosenId = $state('');
	let guide = $derived(chosenId ? guideById(chosenId) : install.guide);

	async function requestInstall(): Promise<void> {
		installing = true;
		const accepted = await install.promptInstall();
		installing = false;

		if (accepted) {
			toasts.success('Приложение установлено');
		}
	}

	async function requestNotifications(): Promise<void> {
		if (!('Notification' in window)) {
			toasts.error('Этот браузер не умеет показывать уведомления');
			return;
		}

		const result = await Notification.requestPermission();

		if (result === 'granted') {
			toasts.success('Уведомления разрешены');
		} else {
			toasts.show('Уведомления можно включить позже в настройках устройства');
		}
	}

	async function copyAddress(): Promise<void> {
		try {
			await copyText(location.origin);
			toasts.success('Адрес скопирован');
		} catch (error) {
			toasts.error(error instanceof Error ? error.message : 'Не удалось скопировать адрес');
		}
	}
</script>

<div class="screen" transition:fade={{ duration: 180 }}>
	<div class="inner">
		<header class="head">
			<div class="mark"><span>A</span></div>
			<h1 class="title">Установите ANDRESOT на устройство</h1>
			<p class="subtitle">
				Приложение откроется своим окном, будет работать без интернета и присылать уведомления
			</p>
		</header>

		{#if install.capability === 'prompt' && !chosenId}
			<!-- Браузер умеет ставить приложение сам: лишние шаги здесь ни к чему. -->
			<div class="card">
				<StepIllustration art="home-icon" />
				<p class="card-text">Нажмите кнопку — приложение появится среди остальных</p>
				<Button variant="primary" size="large" block loading={installing} onclick={requestInstall}>
					Установить приложение
				</Button>
			</div>

			<div class="card">
				<p class="card-text">
					После установки разрешите уведомления, чтобы не пропускать сообщения
				</p>
				<Button variant="secondary" block onclick={requestNotifications}>
					Разрешить уведомления
				</Button>
			</div>
		{:else if install.capability === 'in-app' && !chosenId}
			<!--
				Встроенное окно чужого приложения: установки в нём нет ни у одной
				платформы, и служебных сценариев тоже. Честнее сказать об этом,
				чем показывать шаги, которые заведомо не сработают.
			-->
			<div class="card warning">
				<span class="warning-icon"><Icon name="info" size={28} /></span>
				<p class="card-title">Откройте в браузере</p>
				<p class="card-text">
					Страница открыта во встроенном окне другого приложения — установить отсюда нельзя.
					Откройте этот адрес в обычном браузере и вернитесь сюда.
				</p>
				<code class="address">{location.origin}</code>
				<Button variant="primary" block onclick={() => void copyAddress()}>Скопировать адрес</Button
				>
			</div>
		{:else if guide.unsupported}
			<div class="card warning" aria-label="Браузер не умеет устанавливать приложения">
				<span class="warning-icon"><Icon name="info" size={28} /></span>
				<p class="card-title">{guide.title} не умеет ставить приложения</p>
				<p class="card-text">{guide.note}</p>
				<code class="address">{location.origin}</code>
				<Button variant="primary" block onclick={() => void copyAddress()}>Скопировать адрес</Button
				>
			</div>
		{:else}
			<p class="guide-title">{guide.title}</p>

			<ol class="steps">
				{#each guide.steps as step, index (index)}
					<li class="card step">
						<span class="number">{index + 1}</span>
						<StepIllustration art={step.art} />
						<p class="card-text">{step.text}</p>
					</li>
				{/each}
			</ol>

			{#if guide.note}
				<p class="note">{guide.note}</p>
			{/if}

			<div class="card">
				<p class="card-text">
					После установки разрешите уведомления, чтобы не пропускать сообщения
				</p>
				<Button variant="secondary" block onclick={requestNotifications}>
					Разрешить уведомления
				</Button>
			</div>
		{/if}

		<!--
			Определение по строке браузера иногда ошибается — например, во
			встроенных окнах, которые притворяются обычным браузером. Пусть
			человек найдёт свой случай сам, а не упирается в чужие шаги.
		-->
		<label class="picker">
			<span>У меня другой браузер</span>
			<select bind:value={chosenId} aria-label="Инструкция для другого браузера">
				<option value="">Определить самому ({install.guide.title})</option>
				{#each GUIDES as item (item.id)}
					<option value={item.id}>{item.title}</option>
				{/each}
			</select>
		</label>

		<p class="note">
			Уведомления приходят в установленное приложение через защищённое соединение. Управлять ими
			можно позже в настройках уведомлений.
		</p>

		<footer class="actions">
			<button class="link" onclick={() => install.closeScreen()}>Продолжить в браузере</button>
			<a class="link" href="https://andresot.ru">Перейти на главную</a>
		</footer>
	</div>
</div>

<style>
	.screen {
		position: fixed;
		inset: 0;
		z-index: var(--z-modal);
		overflow-y: auto;
		/* Узор фона тот же, что в ленте сообщений: экран установки —
		   часть приложения, а не отдельная страница. */
		background:
			var(--chat-pattern-image),
			radial-gradient(ellipse 90% 50% at 50% 0%, var(--chat-pattern-1) 0%, transparent 70%),
			var(--background-primary);
		/* Отступы под вырез и полосу жестов iPhone. */
		padding: calc(var(--spacing-2xl) + env(safe-area-inset-top)) var(--spacing-l)
			calc(var(--spacing-2xl) + env(safe-area-inset-bottom));
	}

	.inner {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-l);
		max-width: 520px;
		margin: 0 auto;
	}

	.head {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--spacing-m);
		text-align: center;
	}

	.mark {
		display: flex;
		align-items: center;
		justify-content: center;
		width: var(--size-72);
		height: var(--size-72);
		border-radius: var(--radius-xl);
		background: var(--accent-primary);
		color: #fff;
		font-size: 38px;
		font-weight: var(--font-weight-semibold);
		line-height: 1;
	}

	.title {
		font: var(--text-title);
		text-wrap: balance;
	}

	.subtitle {
		color: var(--text-secondary);
		font: var(--text-body);
		text-wrap: pretty;
	}

	/* Название случая, для которого показаны шаги. */
	.guide-title {
		color: var(--text-secondary);
		font: var(--text-body-medium);
		text-align: center;
	}

	.card {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-m);
		padding: var(--spacing-l);
		border: 1px solid var(--border-subtle);
		border-radius: var(--radius-xl);
		background: var(--background-surface);
	}

	.card-title {
		font: var(--text-subtitle);
	}

	.card-text {
		color: var(--text-secondary);
		font: var(--text-body);
		text-wrap: pretty;
	}

	.warning {
		align-items: flex-start;
	}

	.warning-icon {
		display: flex;
		color: var(--status-warning, var(--accent-primary));
	}

	.address {
		align-self: stretch;
		padding: var(--spacing-m);
		border-radius: var(--radius-m);
		background: var(--background-tertiary);
		color: var(--text-primary);
		font: var(--text-caption);
		overflow-wrap: anywhere;
		user-select: all;
	}

	.steps {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-m);
		list-style: none;
	}

	.step {
		position: relative;
	}

	.number {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		border-radius: var(--radius-round);
		background: var(--accent-primary);
		color: #fff;
		font: var(--text-caption-medium, var(--text-caption));
	}

	.picker {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
		color: var(--text-secondary);
		font: var(--text-caption);
	}

	.picker select {
		padding: var(--spacing-m);
		border: 1px solid var(--border-subtle);
		border-radius: var(--radius-m);
		background: var(--background-surface);
		color: var(--text-primary);
		font: var(--text-body);
	}

	.note {
		color: var(--text-tertiary);
		font: var(--text-caption);
		text-align: center;
		text-wrap: pretty;
	}

	.actions {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--spacing-m);
	}

	.link {
		color: var(--accent-primary);
		font: var(--text-body-medium);
	}
</style>
